/**
 * TASKS — Zercode.Operations task manager wire schemas (Operations :5950, /api/v1/operations/tasks),
 * Zod-parsed at the seam. Mirrors TaskDto / TaskListItemDto / TaskDetailDto / TaskAttachmentDto /
 * TaskFacetsDto (migs 680–681). Plan: ZerEduService docs/task-manager-plan.md.
 *
 * ⚠ Names are TASK-qualified on purpose (`taskStatuses`, not `statuses`): this package re-exports every
 * module flat, so an unqualified name collides with another service's (see `ticketStatuses`).
 */
import { z } from 'zod';

import { pagedListSchema } from './education';

const isoDate = z.string().min(1);

/**
 * The task's CATEGORY since TASK-STATUS (mig 683): open, or done (its status is the last in the task_status order).
 * The status itself is a definition (statusRef); in_progress / cancelled are retired values older rows may carry.
 */
export const taskStatuses = ['open', 'in_progress', 'done', 'cancelled'] as const;
/** TASK-STATUS: the Definitions group that holds the task statuses, in order. */
export const TASK_STATUS_GROUP = 'task_status';
export const taskActiveStatuses = ['open', 'in_progress'] as const;
export const taskPriorities = ['low', 'normal', 'high', 'urgent'] as const;
/** `?due=` buckets — `TaskDueBuckets` on the server. */
export const taskDueBuckets = ['overdue', 'today', 'week', 'later', 'none'] as const;
/** `?view=` — what a tab asks for. `all` and `unassigned` widen to the tenant only for a manager. */
export const taskViews = ['mine', 'created', 'watching', 'unassigned', 'all'] as const;
export const taskOrigins = ['manual', 'system'] as const;
export const taskAttachmentKinds = ['image', 'file'] as const;

/**
 * Server limits (`TaskAttachmentLimits`). The browser checks them first so a refusal reads as a reason,
 * but the server stays the authority — it re-checks size AND reads the type from the bytes.
 */
export const TASK_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const TASK_FILE_MAX_BYTES = 10 * 1024 * 1024;
export const TASK_MAX_ATTACHMENTS = 20;
/** Plan §14.3 allow-list, by extension — a hint only; the server sniffs the bytes. */
export const taskImageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'] as const;
export const taskFileExtensions = [
  '.pdf',
  '.docx',
  '.xlsx',
  '.pptx',
  '.doc',
  '.xls',
  '.ppt',
  '.txt',
  '.csv',
  '.zip'
] as const;

const stringMap = z.record(z.string(), z.string()).nullish();

export const taskSchema = z.object({
  id: z.guid(),
  taskNo: z.string(),
  title: z.string(),
  description: z.string().nullish(),
  /** System tasks: the FE localises the title from `kind` + `titleArgs`; `title` is the fallback. */
  kind: z.string().nullish(),
  titleArgs: stringMap,
  status: z.enum(taskStatuses),
  priority: z.enum(taskPriorities),
  dueAt: isoDate.nullish(),
  remindAt: isoDate.nullish(),
  assigneeRef: z.guid().nullish(),
  /** Snapshot at assignment time — Operations cannot read identity [STD-OWN]. */
  assigneeName: z.string().nullish(),
  createdByRef: z.guid().nullish(),
  createdByName: z.string().nullish(),
  origin: z.enum(taskOrigins),
  sourceService: z.string().nullish(),
  entityType: z.string().nullish(),
  entityRef: z.guid().nullish(),
  entityLabel: z.string().nullish(),
  completedAt: isoDate.nullish(),
  completedByRef: z.guid().nullish(),
  createdAt: isoDate,
  updatedAt: isoDate,
  /** TASK-STATUS: the status DEFINITION (group task_status) + its name when set. status is only open | done. */
  statusRef: z.guid().nullish(),
  statusName: z.string().nullish()
});

export const taskListItemSchema = z.object({
  id: z.guid(),
  taskNo: z.string(),
  title: z.string(),
  kind: z.string().nullish(),
  titleArgs: stringMap,
  status: z.enum(taskStatuses),
  priority: z.enum(taskPriorities),
  dueAt: isoDate.nullish(),
  assigneeRef: z.guid().nullish(),
  assigneeName: z.string().nullish(),
  createdByRef: z.guid().nullish(),
  createdByName: z.string().nullish(),
  origin: z.enum(taskOrigins),
  sourceService: z.string().nullish(),
  entityType: z.string().nullish(),
  entityRef: z.guid().nullish(),
  entityLabel: z.string().nullish(),
  checklistTotal: z.number().int(),
  checklistDone: z.number().int(),
  attachmentCount: z.number().int(),
  createdAt: isoDate,
  updatedAt: isoDate,
  statusRef: z.guid().nullish(),
  statusName: z.string().nullish()
});

export const taskChecklistItemSchema = z.object({
  id: z.guid(),
  sequenceNo: z.number().int(),
  text: z.string(),
  doneAt: isoDate.nullish(),
  doneByRef: z.guid().nullish()
});

/** One timeline line: a person's comment, or a system activity the FE localises from `activity` + args. */
export const taskCommentSchema = z.object({
  id: z.guid(),
  kind: z.enum(['comment', 'activity']),
  authorRef: z.guid().nullish(),
  authorName: z.string().nullish(),
  body: z.string(),
  activity: z.string().nullish(),
  activityArgs: stringMap,
  createdAt: isoDate,
  updatedAt: isoDate
});

export const taskWatcherSchema = z.object({
  userRef: z.guid(),
  userName: z.string().nullish(),
  createdAt: isoDate
});

export const taskAttachmentSchema = z.object({
  id: z.guid(),
  taskId: z.guid(),
  commentId: z.guid().nullish(),
  kind: z.enum(taskAttachmentKinds),
  fileName: z.string(),
  contentType: z.string(),
  sizeBytes: z.number(),
  width: z.number().int().nullish(),
  height: z.number().int().nullish(),
  uploadedByRef: z.guid().nullish(),
  uploadedByName: z.string().nullish(),
  createdAt: isoDate
});

/** What the CALLER may do on this task, so the drawer never offers a button the server refuses. */
export const taskCapabilitiesSchema = z.object({
  canWork: z.boolean(),
  canDelete: z.boolean(),
  isManager: z.boolean()
});

export const taskDetailSchema = z.object({
  task: taskSchema,
  checklist: z.array(taskChecklistItemSchema),
  timeline: z.array(taskCommentSchema),
  watchers: z.array(taskWatcherSchema),
  attachments: z.array(taskAttachmentSchema),
  capabilities: taskCapabilitiesSchema
});

const taskCountByKeySchema = z.object({ key: z.string(), count: z.number().int() });

export const taskFacetsSchema = z.object({
  byStatus: z.array(taskCountByKeySchema),
  byDue: z.array(taskCountByKeySchema),
  total: z.number(),
  myOverdue: z.number().int(),
  myDueToday: z.number().int(),
  /** TASK-STATUS: counts per status definition id ('' = a task created while Definitions was unreachable). */
  byStatusRef: z.array(taskCountByKeySchema).nullish()
});

/**
 * GET /operations/tasks/statuses (TCH-TASKS) — the task_status values in order, for a task user who cannot read
 * Definitions (a teacher). No colour: the server reads them over gRPC, which does not carry one.
 */
export const taskStatusOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  orderNo: z.number(),
  isDefault: z.boolean()
});
export const taskStatusOptionListSchema = z.array(taskStatusOptionSchema);

export const taskListSchema = pagedListSchema(taskListItemSchema);
export const taskAttachmentListSchema = z.array(taskAttachmentSchema);

export type TaskStatus = (typeof taskStatuses)[number];
export type TaskPriority = (typeof taskPriorities)[number];
export type TaskDueBucket = (typeof taskDueBuckets)[number];
export type TaskView = (typeof taskViews)[number];
export type TaskDto = z.infer<typeof taskSchema>;
export type TaskListItem = z.infer<typeof taskListItemSchema>;
export type TaskChecklistItem = z.infer<typeof taskChecklistItemSchema>;
export type TaskComment = z.infer<typeof taskCommentSchema>;
export type TaskWatcher = z.infer<typeof taskWatcherSchema>;
export type TaskAttachment = z.infer<typeof taskAttachmentSchema>;
export type TaskCapabilities = z.infer<typeof taskCapabilitiesSchema>;
export type TaskDetail = z.infer<typeof taskDetailSchema>;
export type TaskFacets = z.infer<typeof taskFacetsSchema>;

/** POST /tasks — the body IS the wire contract (CreateTaskBody). */
export interface CreateTaskRequest {
  title: string;
  description?: string | null;
  priority?: TaskPriority;
  dueAt?: string | null;
  remindAt?: string | null;
  assigneeRef?: string | null;
  assigneeName?: string | null;
  sourceService?: string | null;
  entityType?: string | null;
  entityRef?: string | null;
  entityLabel?: string | null;
  checklist?: string[];
}

/** PATCH /tasks/{id} — null/absent = no change; `clear` names fields to empty (description | dueAt | link). */
export interface PatchTaskRequest {
  title?: string;
  description?: string;
  priority?: TaskPriority;
  dueAt?: string;
  remindAt?: string;
  clear?: ('description' | 'dueAt' | 'link')[];
}

export interface TaskChecklistInput {
  /** Existing item id to keep (its id is its identity); null for a new line. */
  id: string | null;
  text: string;
}
